<script>
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte'
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';
    import Link from '@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte';
    import Modal from '@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte';
    import UserContent from './UserContent/UserContent.svelte';
    import {createAnswer, getQrCode, addKpi} from '../api';
    import {survey, permissions, store, questions, isInActive, isInActiveForRespondents, hasDeadlinePassedForRespondents, hasDeadlinePassed} from '../stores';
    import {loadSurvey} from '../helpers';
    import { get as getStoreObj } from 'svelte/store';
    import {txt} from '../i18n/i18n';
    import { navigateToTakeSurveyPage, navigateToEditSurveyPage, navigateToViewAnswerPage, navigateToPrintSurveyPage} from '../navigation';
    import {tick} from 'svelte';
  
    /** @type {boolean} */
    export let showErrorMessage = false;
    let showQrCode = false;
    let qrCodeImg = undefined;
    let qrCode = undefined;
    
    let isStarting = false;
    let linkTimeoutId;
    let qrTimeoutId;
    $: isNotCompleted = (isNaN(Date.parse($survey.completed)) || Date.parse($survey.completed) < 0);
    $: canTakeSurvey = $permissions.canParticipate && $questions.length > 0 && isNotCompleted;
    

    async function takeSurvey() {
        if (isStarting) {
                return;
        }
        try {
            isStarting = true;
            showErrorMessage = false;
            let response = await createAnswer();
            if (response.questionAnswers) {
                showErrorMessage = await loadSurvey();
            } else {
            survey.update(currentSurvey => {
                    currentSurvey.answerId = response.answerId;
                    return currentSurvey;
                });
            }
        } catch {
            showErrorMessage = true;
            isStarting = false;
            return;
        } 
    
        if (canTakeSurvey && !showErrorMessage) {
            navigateToTakeSurveyPage();
        }
        isStarting = false;
    }

    function getBaseUrl(hash) {
        const s = getStoreObj(store);
        return s.openExternUrl + hash;
    }

    function copyUrl(url) {
        navigator.clipboard.writeText(url);
        document.getElementById('CopyLinkFeedback').innerHTML = txt('Link_copied_to_clipboard');
        if(!linkTimeoutId) {
            clearTimeout(linkTimeoutId);
        }
        linkTimeoutId = setTimeout(()=>{removeFeedback('CopyLinkFeedback')}, 5000);
    }

    function copyQRCode(){
        try {
            navigator.clipboard.write([
            new ClipboardItem({
                'image/png': qrCode.blob
                })
            ]);
            document.getElementById('CopyQRCodeFeedback').innerHTML = txt('QR_code_copied_to_clipboard');
            if(!qrTimeoutId) {
                clearTimeout(qrTimeoutId);
            }
            qrTimeoutId = setTimeout(()=>{removeFeedback('CopyQRCodeFeedback')}, 5000);
        } catch (error) {
            console.error(error);
        }
    }

    function removeFeedback(id) {
        const feedbackArea = document.getElementById(id);
        if (feedbackArea) {
            feedbackArea.innerHTML = '';
        }
    }

    function print() {
        try {
            addKpi('PrintSurvey');
        } catch {
            console.error("Error adding kpi");
        }
        navigateToPrintSurveyPage();
    }

    async function showQrCodeImage(url) {
        if (showQrCode) {
            showQrCode = false;
            return;
        }
        if (!qrCode) {
            qrCode = await getQrCode(url);
        }
        showQrCode = true;
        await tick();
        qrCodeImg.src = qrCode.url;
    }
</script>
<Modal enabled={showQrCode} 
        titleText={txt('Share_survey')}
        confirmText={txt('Close')}
        showCancel={false}
        confirmButtonType="secondary"
        on:close={() => {showQrCode = false;}}>
    <div slot="banner">
        <Banner visible={$isInActive || $hasDeadlinePassed} 
            close={txt('Close')} closable={false} 
            type="info" 
            text={ $isInActive && !$hasDeadlinePassed ? txt('Remember_visible') : txt('Remember_visible_and_deadline')  } />
    </div>
    <div slot="body" class="qrcode">
        <h3>{txt('Link_to_survey')}</h3>
        <p class="survey-share-item">
            {getBaseUrl($survey.openExternHash)}
        </p>
        <div class="copy-link-container">
            <IconButton htmlType="button" iconClass='copy-button-icon' text="{txt('Copy_public_link')}" type="secondary" on:click={() => copyUrl(getBaseUrl($survey.openExternHash))} />
            <span id="CopyLinkFeedback" aria-live="polite"></span>
        </div>
        <h3>{txt('QR_code')}</h3>
        <div class="copy-qr-container">
            <IconButton htmlType="button" iconClass='copy-button-icon' text="{txt('Copy_QR_code')}" type="secondary" on:click={() => copyQRCode()} />
            <span id="CopyQRCodeFeedback" aria-live="polite"></span>
        </div>
        <p class="survey-share-item">
            <img id="QRCodeImage" alt="{txt('QR_code')}" bind:this={qrCodeImg} />
        </p>
    </div>
</Modal>
<div class="flow">
    {#if $permissions.canModify || $permissions.canModifyInstance || $permissions.canEvaluate}
    <div>
        <h3 class="header">{txt('Introduction')}</h3>
        <p class="content">
            <UserContent renderAsHtml={$survey.hasHtml} text={$survey.description || ''}></UserContent>
        </p>
    </div>
    <div>
        <h3 class="header">{txt('Survey_type')}</h3>
        <p class="content">
            {#if $survey.respondentType === 1}
                {txt('Not_anonymous')}
            {:else if $survey.respondentType === 2}
                {txt('Anonymous')}
            {:else if $survey.respondentType === 3}
                {txt('External_and_anonymous')}
            {/if}
        </p>
    </div>
    {:else if $permissions.canRead}
        <div>
            <h3 class="header">{txt('Introduction')}</h3>
            <p class="content">
                <UserContent renderAsHtml={$survey.hasHtml} text={$survey.description} />
            </p>
        </div>
        <div>
            <h3 class="header">{txt('Survey_type')}</h3>
            <p class="content">
                {#if $survey.respondentType === 1}
                    {txt('Not_anonymous')}    
                {:else if $survey.respondentType === 2 || $survey.respondentType === 3}
                    {txt('Anonymous')}
                {/if}
            </p> 
        </div>   
    {/if}
</div>

<div class="footer-actions">
    <div class="footer-actions-left">
        {#if canTakeSurvey}
            <Button text={$survey.answerId > 0 ? txt('Continue_survey') : txt('Take_survey')} disabled={$isInActiveForRespondents || $hasDeadlinePassedForRespondents} htmlType="button" type="primary" on:click={takeSurvey} loading={isStarting} />
        {:else if $survey.answerId > 0}
           <Link kind="standalone" href="#show_result" on:click={() => navigateToViewAnswerPage($survey.answerId, 'tabSurvey')} text="{txt('Show_response')}" />
        {/if}
        {#if $permissions.canEvaluate && $survey.respondentType === 3}
            <Button htmlType="button" text="{txt('Share')}" type="secondary" on:click={showQrCodeImage(getBaseUrl($survey.openExternHash))} />
        {/if}
        {#if $permissions.canModify || $permissions.canModifyInstance}
            <Link kind="standalone" iconClass="edit-link" href="#editsurvey" on:click={() => navigateToEditSurveyPage()} text="{txt('Edit_survey')}" />
        {/if}   
    </div>
    
    {#if $permissions.canModify || $permissions.canModifyInstance || $permissions.canEvaluate}
        <div class="footer-actions-right">
            <Link kind="standalone" iconClass="print-link" href="#print" on:click={() => print()} text="{txt('Print')}" />
        </div>
    {/if}
</div>